<template>
  <DefineDetailSectionTemplate #default="{ detailSection }">
    <BaseCard>
      <h3
        v-if="detailSection.label"
        class="mb-3 text-sm font-semibold text-background-fg/70"
      >
        {{ detailSection.label }}
      </h3>
      <dl class="space-y-2">
        <template
          v-for="detail in detailSection.details"
          :key="detail.label"
        >
          <div
            :class="[
              detail.value === true
                ? 'flex justify-between bg-positive/8'
                : '',
              'flex flex-wrap items-center justify-between rounded px-2 py-1',
            ]"
          >
            <dt
              class="text-left text-xs text-background-fg/50"
            >
              {{ detail.label }}
            </dt>
            <dd
              class="hide-scrollbar max-w-full shrink-0 overflow-x-auto"
            >
              <template v-if="detail.value === true">
                <div
                  class="flex size-4 items-center justify-center rounded"
                >
                  <IconCheck class="text-positive" />
                </div>
              </template>
              <template v-else-if="detail.value === false">
                <div
                  class="flex size-4 items-center justify-center rounded"
                >
                  <IconXCircle
                    class="text-background-fg/25"
                  />
                </div>
              </template>
              <template
                v-else-if="
                  detail.value !== undefined &&
                  detail.value !== null
                "
              >
                <span class="text-sm font-medium">
                  {{ detail.value }}
                </span>
              </template>
              <template v-else> - </template>
            </dd>
          </div>
        </template>
      </dl>
    </BaseCard>
  </DefineDetailSectionTemplate>

  <template v-if="isFetching">
    <div class="size-32">
      <BaseInnerLoading spinner-class="size-8" />
    </div>
  </template>
  <template v-else-if="data">
    <div class="flex w-full justify-between pt-4">
      <h2
        :class="[
          'line-clamp-1 px-4 text-lg font-semibold',
          data.isActive
            ? 'text-background-fg'
            : 'text-background-fg/60',
        ]"
      >
        {{ data.businessName }}
        <span v-if="!data.isActive">(disabled)</span>
      </h2>
    </div>
    <div class="w-full max-w-3xl p-4">
      <div class="mb-4">
        <div class="border-b border-gray-200">
          <nav
            class="-mb-px grid grid-cols-2 gap-4 sm:grid-cols-4"
            aria-label="Tabs"
          >
            <button
              v-for="tab in TABS"
              :key="tab"
              :class="[
                currentTab === tab
                  ? 'border-primary text-primary'
                  : 'border-transparent text-background-fg/70 hover:border-background-fg/8 hover:text-background-fg/87',
                'line-clamp-1 whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium',
              ]"
              @click="goToTab(tab)"
              >{{ tab }}</button
            >
          </nav>
        </div>
      </div>
      <template v-if="currentTab === 'Details'">
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div class="flex flex-col space-y-4">
            <DetailSectionTemplate
              :detail-section="businessDetails"
            />
          </div>
          <div class="flex flex-col space-y-4">
            <DetailSectionTemplate
              :detail-section="customerDetails"
            />
          </div>
        </div>
      </template>

      <template v-else-if="currentTab === 'Features'">
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div class="flex flex-col space-y-4">
            <DetailSectionTemplate
              :detail-section="paymentFeaturesDetails"
            />
          </div>
          <div class="flex flex-col space-y-4">
            <DetailSectionTemplate
              :detail-section="salesFeaturesDetails"
            />
            <DetailSectionTemplate
              :detail-section="vatFeaturesDetails"
            />
          </div>
        </div>
      </template>
      <template v-else-if="currentTab === 'Statistics'">
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <DetailSectionTemplate
            :detail-section="statsDetails"
          />
          <dl>
            <dt
              class="mb-2 text-sm font-semibold text-background-fg/87"
            >
              Busiest Days (All Time)
            </dt>
            <dd class="flex justify-center">
              <ChartHeatmap
                :data="dayHeatmapData"
                :labels="dayHeatmapLabels"
                class="w-[21.5rem]"
              />
            </dd>
            <dt
              class="mb-2 text-sm font-semibold text-background-fg/87"
            >
              Busiest Days (Last Week)
            </dt>
            <dd class="flex justify-center">
              <ChartHeatmap
                :data="dayHeatmapDataLastWeek"
                :labels="dayHeatmapLabels"
                class="w-[21.5rem]"
              />
            </dd>
          </dl>
        </div>
      </template>

      <template v-else-if="currentTab === 'Terminals'">
        <MerchantGroupMerchantDetailTerminals
          :schema-name="schemaName"
        />
      </template>
    </div>
  </template>
</template>

<script setup lang="ts">
import { createReusableTemplate } from "@vueuse/core"

type DetailSection = {
  label: string
  details: { label: string; value: unknown }[]
}

const props = defineProps<{
  schemaName: string
}>()

const [DefineDetailSectionTemplate, DetailSectionTemplate] =
  createReusableTemplate<{ detailSection: DetailSection }>()

const { data, isFetching } = useMerchantGroupMerchant(
  toRef(props, "schemaName")
)

const { formatToCurrencyString } = useCurrency()
const TABS = [
  "Details",
  "Features",
  "Statistics",
  "Terminals",
] as const

type Tab = (typeof TABS)[number]
const currentTab = ref<Tab>("Details")

const route = useRoute()

onMounted(() => {
  const initialTabParam: Tab | undefined = TABS.includes(
    route.query.initialTab as any
  )
    ? (route.query.initialTab as any)
    : undefined

  if (initialTabParam) {
    currentTab.value = initialTabParam
  }
})

const goToTab = (tab: Tab) => {
  currentTab.value = tab
}

const businessDetails = computed<DetailSection>(() => ({
  label: "Business",
  details: [
    {
      label: "Business Name",
      value: data.value?.businessName,
    },
    {
      label: "Currency",
      value: data.value?.currency,
    },
  ],
}))

const customerDetails = computed<DetailSection>(() => ({
  label: "Contact Details",
  details: [
    {
      label: "Name",
      value: data.value?.customerName,
    },
    {
      label: "Email",
      value: data.value?.customerEmail,
    },
    {
      label: "Phone",
      value: data.value?.customerPhone,
    },
  ],
}))

const paymentFeaturesDetails = computed<DetailSection>(
  () => ({
    label: "Payments",
    details: [
      {
        label: "Are cash payments enabled?",
        value: data.value?.isCashEnabled,
      },
      {
        label: "Are split payments enabled?",
        value: data.value?.isSplitPaymentsEnabled,
      },
      {
        label: "Is grautuity enabled?",
        value: data.value?.isGratuityEnabled,
      },
      {
        label: "Are unlinked refunds enabled?",
        value: data.value?.isUnlinkedRefundsEnabled,
      },
      {
        label: "Is service charge enabled?",
        value: data.value?.isServiceChargeEnabled,
      },
    ],
  })
)

const vatFeaturesDetails = computed<DetailSection>(() => ({
  label: "VAT",
  details: [
    {
      label: "Is VAT enabled?",
      value: data.value?.isVatEnabled,
    },
    {
      label: "VAT Number",
      value: data.value?.vatNumber,
    },
  ],
}))

const salesFeaturesDetails = computed<DetailSection>(
  () => ({
    label: "Sales",
    details: [
      {
        label: "Are products enabled?",
        value: data.value?.isProductsEnabled,
      },
      {
        label: "Are cashiers enabled?",
        value: data.value?.isCashiersEnabled,
      },
    ],
  })
)

const dayHeatmapData = computed(
  () =>
    data.value?.transactionCountsByWeekday.map(
      ({ dayOfWeek, transactionCount }) => ({
        x: ISO_DAYS[dayOfWeek],
        y: transactionCount,
      })
    ) || []
)

const dayHeatmapDataLastWeek = computed(
  () =>
    data.value?.transactionCountsByWeekdayLastWeek.map(
      ({ dayOfWeek, transactionCount }) => ({
        x: ISO_DAYS[dayOfWeek],
        y: transactionCount,
      })
    ) || []
)

const statsDetails = computed<DetailSection>(() => ({
  label: "",
  details: [
    {
      label: "Merchant Created",
      value: data.value?.createdAt
        ? `${formatToDate(data.value?.createdAt)}`
        : "-",
    },
    {
      label: "First Transacted At",
      value: data.value?.firstTransactedAt
        ? `${formatToDate(data.value?.firstTransactedAt)}`
        : "-",
    },
    {
      label: "Last Transacted At",
      value: data.value?.lastTransactedAt
        ? `${formatToDate(data.value?.lastTransactedAt)}`
        : "-",
    },
    {
      label: "Transacted Total",
      value: data.value
        ? formatToCurrencyString(
            data.value.transactionTotal || 0,
            data.value.currency
          )
        : "-",
    },
    {
      label: "Number of Transactions",
      value: data.value?.transactionCount,
    },
    {
      label: "Average Transaction",
      value: data.value
        ? formatToCurrencyString(
            (data.value.transactionTotal || 0) /
              (data.value.transactionCount || 1),
            data.value.currency
          )
        : "-",
    },
  ],
}))

const ISO_DAYS = {
  1: "Mo",
  2: "Tu",
  3: "We",
  4: "Th",
  5: "Fr",
  6: "Sa",
  7: "Su",
}
const dayHeatmapLabels = Object.values(ISO_DAYS)
</script>

<style scoped>
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
</style>
